/* File: src\contexts\profile\profileDataFetching.js */
import { useState, useEffect, useRef } from 'react';
import {
  collection,
  doc,
  query,
  orderBy,
  where,
  onSnapshot,
  Timestamp, // Import Timestamp to check its type
} from 'firebase/firestore';
import { db } from '../../firebaseConfig'; // Adjust path if needed

// Key for storing active profile ID in localStorage
const LOCAL_STORAGE_KEY = 'dualNBackActiveProfileId';

// Helper to safely convert timestamp to ISO string
export const safeToISOString = (timestamp) => {
    // Added check specifically for Firestore Timestamp object
    // This check should ideally be first as it's a common type from Firestore
    if (timestamp instanceof Timestamp) {
        try {
            return timestamp.toDate().toISOString();
        } catch (e) {
            // Log error if converting Firestore Timestamp fails
            console.error("Timestamp.toDate().toISOString() failed:", timestamp, e); // Keep error log
            return null;
        }
    }
    // Keep original checks for Date objects, strings, or numbers
    try {
        if (timestamp instanceof Date) {
            return timestamp.toISOString();
        } else if (typeof timestamp === 'string' || typeof timestamp === 'number') {
            const date = new Date(timestamp);
            if (!isNaN(date.getTime())) { // Check if parsing was successful
                return date.toISOString();
            }
        }
    } catch (e) {
        // Log general conversion errors
        console.error("Timestamp conversion failed:", timestamp, e); // Keep error log
    }
    // Do not log warning for unhandled cases in production
    return null; // Return null if conversion fails or input is invalid/unhandled
};

/**
 * Hook to load the list of user profiles using a real-time listener (onSnapshot).
 * It also determines the initially active profile based on localStorage and validity.
 * Provides the list of profiles and the determined active ID to ProfileContext.
 */
export const useProfileListLoader = (userId, isAuthLoading) => {
  const [profiles, setProfiles] = useState([]);
  const [isLoadingProfiles, setIsLoadingProfiles] = useState(true);
  const [profilesError, setProfilesError] = useState(null);
  const [determinedActiveProfileId, setDeterminedActiveProfileId] = useState(null);
  const listenerUserId = useRef(null);
  const initialStoredId = useRef(null);
  const initialReadDone = useRef(false);

  useEffect(() => {
    let unsubscribe = () => {};

    // --- Authentication and User State Handling ---
    // 1. Wait for authentication to resolve
    if (isAuthLoading) {
        if (!isLoadingProfiles) setIsLoadingProfiles(true);
        initialReadDone.current = false; // Reset initial read flag if auth is loading
        return;
    }

    // 2. Read initial stored ID once auth is ready
    if (!initialReadDone.current && userId) {
        try {
            initialStoredId.current = typeof window !== 'undefined' ? localStorage.getItem(LOCAL_STORAGE_KEY) : null;
        } catch (storageError) {
            console.error("[useProfileListLoader Initial Read] Error reading localStorage:", storageError); // Keep error log
            initialStoredId.current = null; // Assume null on error
        }
        initialReadDone.current = true; // Mark as read
    }
    // Also mark as done and clear if user logs out after being logged in
    if (!userId && initialReadDone.current) {
         initialReadDone.current = false;
         initialStoredId.current = null;
    }

    // 3. Handle user logout: Clear state, unsubscribe, remove localStorage
    if (!userId) {
        if (profiles.length > 0) setProfiles([]);
        if (determinedActiveProfileId !== null) setDeterminedActiveProfileId(null);
        if (profilesError !== null) setProfilesError(null);
        if (isLoadingProfiles) setIsLoadingProfiles(false);
        if (listenerUserId.current) {
            unsubscribe(); // Call cleanup from previous run if applicable
            listenerUserId.current = null;
        }
        if (typeof window !== 'undefined') {
             try { if (localStorage.getItem(LOCAL_STORAGE_KEY)) { localStorage.removeItem(LOCAL_STORAGE_KEY); } }
             catch (storageError) { console.error("[useProfileListLoader Logout] Error clearing localStorage:", storageError); } // Keep error log
        }
        return; // Stop execution if no user
    }

    // 4. Avoid redundant listener setup for the same user
    if (listenerUserId.current === userId) {
        if (isLoadingProfiles) setIsLoadingProfiles(false); // Ensure loading state is false if we skip setup
        return;
    }

    // --- Setup Firestore Listener ---
    setIsLoadingProfiles(true);
    setProfilesError(null);
    listenerUserId.current = userId; // Track the user ID for this listener

    const profilesCollectionRef = collection(db, 'users', userId, 'profiles');
    const qProfiles = query(profilesCollectionRef, orderBy('createdAt', 'asc'));

    // Attach the listener
    unsubscribe = onSnapshot(qProfiles,
      (querySnapshot) => {
        // --- Snapshot Handler START ---
        if (listenerUserId.current !== userId) { // Check for stale listener
            // Removed stale snapshot warning log
            return;
        }

        // Removed snapshot receipt log

        // Map Firestore documents to profile objects
        const loadedProfiles = querySnapshot.docs.map((doc) => {
            const data = doc.data();
            const profileId = doc.id; // Get profile ID

            // Removed raw cstHistory log
            const rawHistoryArray = Array.isArray(data.cstHistory) ? data.cstHistory : [];

            // Convert timestamps safely
            const createdAtISO = safeToISOString(data.createdAt);
            const updatedAtISO = safeToISOString(data.updatedAt);

            // Process cstHistory: Map entries and filter out those with invalid timestamps
            const cstHistoryData = rawHistoryArray.map((entry, index) => {
                  const originalTimestamp = entry.timestamp;
                  const convertedTimestamp = safeToISOString(originalTimestamp); // Attempt conversion
                  // Removed pre-filter log
                  return {
                      ...entry, // Keep all original fields from the entry
                      id: entry.id || null, // <<< Reverted fallback ID to null
                      timestamp: convertedTimestamp // Use the converted timestamp (which might be null)
                  };
              })
              .filter((entry, index) => { // Filter based on the conversion result
                  const keep = entry.timestamp !== null;
                  // Removed filtering decision log/warning
                  return keep; // Keep only entries where timestamp conversion succeeded
              });

             // Removed final processed cstHistory log

            // Return the structured profile object
            return {
              id: profileId, // Use the captured profileId
              username: data.username || null,
              currentNBack: data.currentNBack ?? 2,
              currentDifficulty: data.currentDifficulty ?? 0.0,
              highestNBack: Math.max(data.highestNBack ?? 2, data.currentNBack ?? 2),
              consecutiveSuccesses: data.consecutiveSuccesses ?? 0,
              createdAt: createdAtISO,
              updatedAt: updatedAtISO,
              journalTags: Array.isArray(data.journalTags) ? data.journalTags : [],
              cstHistory: cstHistoryData, // Assign the processed (mapped and filtered) history
              cstFamiliarizationCount: data.cstFamiliarizationCount ?? 0,
              lastCstAssessmentTimestamp: data.lastCstAssessmentTimestamp ? safeToISOString(data.lastCstAssessmentTimestamp) : null,
              tutorialsCompleted: typeof data.tutorialsCompleted === 'object' && data.tutorialsCompleted !== null ? data.tutorialsCompleted : {},
              // --- <<< START: Integrated Loading of New Fields >>> ---
              highestNBackGood: data.highestNBackGood ?? null, // Load the N-back level (default null)
              highestAchievedGoodSessionId: data.highestAchievedGoodSessionId ?? null // Load the session ID (default null)
              // --- <<< END: Integrated Loading of New Fields >>> ---
            };
        });
        // --- End Mapping ---

        // Update local state with the loaded profiles
        setProfiles(loadedProfiles);

        // --- Determine/Re-validate Active Profile ID ---
        let determinedIdForThisSnapshot = null;
        const profileIds = loadedProfiles.map(p => p.id);
        let idToCheck = initialStoredId.current;
        // If initial read was done but stored ID was null, re-check current localStorage
        if (idToCheck === null && initialReadDone.current) {
            try { idToCheck = typeof window !== 'undefined' ? localStorage.getItem(LOCAL_STORAGE_KEY) : null; }
            catch (e) { console.error("[useProfileListLoader Determinate] Error reading current localStorage:", e); idToCheck = null; } // Keep error log
        }
        const isStoredIdValid = idToCheck && profileIds.includes(idToCheck);

        if (isStoredIdValid) {
            determinedIdForThisSnapshot = idToCheck;
        } else if (loadedProfiles.length > 0) {
            determinedIdForThisSnapshot = loadedProfiles[0].id; // Fallback to first profile
            // Correct localStorage if fallback occurs
            try { if (typeof window !== 'undefined') { if (determinedIdForThisSnapshot) { localStorage.setItem(LOCAL_STORAGE_KEY, determinedIdForThisSnapshot); } else if (localStorage.getItem(LOCAL_STORAGE_KEY)) { localStorage.removeItem(LOCAL_STORAGE_KEY); } } }
            catch (storageError) { console.error("[useProfileListLoader Determinate] Error correcting localStorage:", storageError); } // Keep error log
        } else {
            determinedIdForThisSnapshot = null; // No profiles exist
            // Ensure localStorage is clear
            try { if (typeof window !== 'undefined' && localStorage.getItem(LOCAL_STORAGE_KEY)) { localStorage.removeItem(LOCAL_STORAGE_KEY); } }
            catch (storageError) { console.error("[useProfileListLoader Determinate] Error clearing localStorage:", storageError); } // Keep error log
        }
        // Clear the initial ref *after* the first determination attempt using it
        if (initialStoredId.current !== null) { initialStoredId.current = null; }
        // Update the determined ID state if it has changed
        setDeterminedActiveProfileId(prevDeterminedId => { return determinedIdForThisSnapshot !== prevDeterminedId ? determinedIdForThisSnapshot : prevDeterminedId; });
        // --- End Determination ---

        setIsLoadingProfiles(false); // Profiles are now loaded
        // --- Snapshot Handler END ---
      },
      (err) => { // Error handler for the profile listener
        if (listenerUserId.current !== userId) { /* Removed stale error warning log */ return; }
        console.error('[useProfileListLoader Error] Error listening to profiles:', err); // Keep error log
        setProfilesError('Failed to load profiles.'); setProfiles([]); setDeterminedActiveProfileId(null);
        try { if (typeof window !== 'undefined') localStorage.removeItem(LOCAL_STORAGE_KEY); } catch (e) { /* ignore */ }
        setIsLoadingProfiles(false); // Stop loading on error
        listenerUserId.current = null; // Reset listener tracking on error
      }
    );

    // Cleanup function for the useEffect hook
    return () => {
      unsubscribe(); // Detach the listener
      listenerUserId.current = null; // Clear user tracking
      initialReadDone.current = false; // Reset read flag on cleanup/user change
    };
  }, [userId, isAuthLoading]); // Dependencies for the main effect

  // Return the state managed by this hook
  return { profiles, isLoadingProfiles, profilesError, determinedActiveProfileId };
};


/**
 * Hook to load subcollections (sessions, journal, friends, etc.) for the active profile.
 * Uses real-time listeners.
 */
export const useSubcollectionListeners = (userId, activeProfileId, isLoadingProfiles) => {
    const [activeSessions, setActiveSessions] = useState([]);
    const [activeJournal, setActiveJournal] = useState([]);
    const [activeFriends, setActiveFriends] = useState([]);
    const [activeRequestsReceived, setActiveRequestsReceived] = useState([]);
    const [activeRequestsSent, setActiveRequestsSent] = useState([]);
    const [isLoadingActiveSubcollections, setIsLoadingActiveSubcollections] = useState(true);
    const [subcollectionError, setSubcollectionError] = useState(null);
    const listenersRef = useRef({ unsubFuncs: [], profileId: null });
    const isMountedRef = useRef(true);
    const initialSnapshotReceivedRef = useRef({
        sessions: false, journal: false, friends: false, requestsReceived: false, requestsSent: false
    });
    const TOTAL_SUBCOLLECTIONS = 5; // Ensure this matches the number of listeners attached

    // Effect to track component mount status
    useEffect(() => {
        isMountedRef.current = true;
        return () => { isMountedRef.current = false; };
    }, []);

    // Main effect for attaching/detaching listeners
    useEffect(() => {
        // --- Helper to check if all initial loads are done ---
        const checkAllInitialLoadsComplete = () => {
            if (!isMountedRef.current) return;
            const flags = initialSnapshotReceivedRef.current;
            const allReceived = Object.values(flags).every(status => status === true);
            if (allReceived && isMountedRef.current) { // Double check mount before state set
                 setIsLoadingActiveSubcollections(false);
            }
        };

        // --- Helper to process snapshot data ---
        const processSnapshot = (snapshot, setter, collectionName, snapshotStatusKey) => {
             // Check for stale listener first
             if (listenersRef.current.profileId !== activeProfileId) {
                  // Removed stale snapshot warning log
                  return;
             }
             // Removed snapshot receipt log

             try {
                const items = snapshot.docs.map(doc => {
                    const data = doc.data();
                    // *** FULL Recursive timestamp conversion helper ***
                    const convertTimestampsRecursive = (obj) => {
                        if (!obj) return obj;
                        if (obj instanceof Timestamp) return safeToISOString(obj);
                        if (obj instanceof Date) return safeToISOString(obj);
                        if (Array.isArray(obj)) return obj.map(convertTimestampsRecursive);
                        if (typeof obj === 'object' && obj.constructor === Object) {
                            const newObj = {};
                            for (const key in obj) {
                                if (Object.prototype.hasOwnProperty.call(obj, key)) {
                                    newObj[key] = convertTimestampsRecursive(obj[key]);
                                }
                            }
                            return newObj;
                        }
                        return obj;
                    };
                    // *** END convertTimestampsRecursive ***
                    const convertedData = convertTimestampsRecursive(data);
                    return { id: doc.id, ...convertedData };
                });

                // Apply sorting based on collection type
                if (collectionName === 'Sessions' || collectionName === 'Journal') {
                    items.sort((a, b) => new Date(b.timestamp || b.recordedAt || 0) - new Date(a.timestamp || a.recordedAt || 0));
                } else if (collectionName === 'Friends') {
                    items.sort((a, b) => (a.friendUsername || '').localeCompare(b.friendUsername || ''));
                } else if (collectionName === 'Received Requests' || collectionName === 'Sent Requests') {
                    items.sort((a, b) => new Date(b.requestedAt || 0) - new Date(a.requestedAt || 0));
                }

                // Update state only if component is still mounted
                if (isMountedRef.current) {
                    setter(items); // Update state
                    // Mark initial snapshot received and check if all are done
                    if (!initialSnapshotReceivedRef.current[snapshotStatusKey]) {
                        initialSnapshotReceivedRef.current[snapshotStatusKey] = true;
                        checkAllInitialLoadsComplete();
                    }
                } else {
                     // Removed unmounted component warning log
                }

             } catch (processingError) {
                 console.error(`[SubListener Process] Error processing ${collectionName} for ${activeProfileId}:`, processingError); // Keep error log
                 if (isMountedRef.current) setSubcollectionError(prev => prev || `Error processing ${collectionName} data.`);
                 // Mark as received even on error to avoid blocking
                 if (!initialSnapshotReceivedRef.current[snapshotStatusKey]) {
                      initialSnapshotReceivedRef.current[snapshotStatusKey] = true;
                      checkAllInitialLoadsComplete();
                 }
             }
        };

        // --- Helper to handle listener errors ---
        const handleError = (err, collectionName, snapshotStatusKey) => {
             if (listenersRef.current.profileId !== activeProfileId) { /* Removed stale error warning log */ return; }
             console.error(`[SubListener ERROR] Error listening to ${collectionName} for ${activeProfileId}:`, err); // Keep error log
             if (isMountedRef.current) setSubcollectionError(prev => prev || `Failed to load ${collectionName}.`);
             // Mark as received on error to potentially unblock loading state
             if (!initialSnapshotReceivedRef.current[snapshotStatusKey]) {
                 initialSnapshotReceivedRef.current[snapshotStatusKey] = true;
                 checkAllInitialLoadsComplete();
             }
        };

        // --- Effect Logic ---
        // 1. Handle Invalid State (No user or profile ID)
        if (!userId || !activeProfileId) {
            if (listenersRef.current.profileId) {
                // Removed cleanup reason log
                listenersRef.current.unsubFuncs.forEach(unsub => unsub());
                listenersRef.current = { unsubFuncs: [], profileId: null };
                if (isMountedRef.current) { /* Reset state */
                     if (activeSessions.length > 0) setActiveSessions([]);
                     if (activeJournal.length > 0) setActiveJournal([]);
                     if (activeFriends.length > 0) setActiveFriends([]);
                     if (activeRequestsReceived.length > 0) setActiveRequestsReceived([]);
                     if (activeRequestsSent.length > 0) setActiveRequestsSent([]);
                     if (subcollectionError !== null) setSubcollectionError(null);
                     if (isLoadingActiveSubcollections) setIsLoadingActiveSubcollections(false);
                }
                initialSnapshotReceivedRef.current = { sessions: false, journal: false, friends: false, requestsReceived: false, requestsSent: false };
            } else {
                 if (isMountedRef.current && isLoadingActiveSubcollections) setIsLoadingActiveSubcollections(false);
            }
            return; // Stop execution
        }
        // 2. Handle Profile List Loading
        if (isLoadingProfiles) {
            // Removed waiting log
             if (isMountedRef.current && !isLoadingActiveSubcollections) setIsLoadingActiveSubcollections(true);
            return; // Wait for profiles to load
        }
        // 3. Handle Already Attached State
        if (listenersRef.current.profileId === activeProfileId) {
            // Removed already attached log
            checkAllInitialLoadsComplete(); // Re-check completion status
            return; // Nothing more to do
        }

        // 4. Handle New Profile Attachment
        // Removed attaching listeners log
        if (listenersRef.current.profileId) { /* Cleanup previous listeners */ listenersRef.current.unsubFuncs.forEach(unsub => unsub()); }
        if (isMountedRef.current) { // Reset state for the new profile
            if (!isLoadingActiveSubcollections) setIsLoadingActiveSubcollections(true);
            setSubcollectionError(null);
            setActiveSessions([]); setActiveJournal([]); setActiveFriends([]); setActiveRequestsReceived([]); setActiveRequestsSent([]);
            initialSnapshotReceivedRef.current = { sessions: false, journal: false, friends: false, requestsReceived: false, requestsSent: false };
        }

        const basePath = `users/${userId}/profiles/${activeProfileId}`;
        const newUnsubFuncs = [];

        // *** Attach ALL subcollection listeners ***
        try {
            // Removed attaching specific listener logs
            newUnsubFuncs.push(onSnapshot( query(collection(db, basePath, 'sessions'), orderBy("recordedAt", "desc")), snap => processSnapshot(snap, setActiveSessions, 'Sessions', 'sessions'), err => handleError(err, 'Sessions', 'sessions') ));
            newUnsubFuncs.push(onSnapshot( query(collection(db, basePath, 'journal'), orderBy("timestamp", "desc")), snap => processSnapshot(snap, setActiveJournal, 'Journal', 'journal'), err => handleError(err, 'Journal', 'journal') ));
            newUnsubFuncs.push(onSnapshot( query(collection(db, basePath, 'friends'), orderBy("friendUsername", "asc")), snap => processSnapshot(snap, setActiveFriends, 'Friends', 'friends'), err => handleError(err, 'Friends', 'friends') ));
            newUnsubFuncs.push(onSnapshot( query(collection(db, basePath, 'friendRequestsReceived'), where("status", "==", "pending"), orderBy("requestedAt", "desc")), snap => processSnapshot(snap, setActiveRequestsReceived, 'Received Requests', 'requestsReceived'), err => handleError(err, 'Received Requests', 'requestsReceived') ));
            newUnsubFuncs.push(onSnapshot( query(collection(db, basePath, 'friendRequestsSent'), where("status", "==", "pending"), orderBy("requestedAt", "desc")), snap => processSnapshot(snap, setActiveRequestsSent, 'Sent Requests', 'requestsSent'), err => handleError(err, 'Sent Requests', 'requestsSent') ));

            listenersRef.current = { unsubFuncs: newUnsubFuncs, profileId: activeProfileId };
            // Removed all listeners initiated log

        } catch (error) {
            console.error(`[SubListener Effect] FATAL ERROR attaching listeners for ${activeProfileId}:`, error); // Keep error log
            if (isMountedRef.current) { /* Set error, stop loading */
                setSubcollectionError("Critical error setting up listeners.");
                setIsLoadingActiveSubcollections(false);
            }
             newUnsubFuncs.forEach(unsub => unsub()); // Clean up any partially attached listeners
             listenersRef.current = { unsubFuncs: [], profileId: null }; // Reset ref
             initialSnapshotReceivedRef.current = { sessions: false, journal: false, friends: false, requestsReceived: false, requestsSent: false }; // Reset flags
        }
        // *** END attaching listeners ***

        // --- Cleanup function for this useEffect ---
        return () => {
            const profileIdAtCleanup = listenersRef.current.profileId;
            if (profileIdAtCleanup) {
                 // Removed cleanup log
                 listenersRef.current.unsubFuncs.forEach(unsub => unsub());
                 listenersRef.current = { unsubFuncs: [], profileId: null };
                 initialSnapshotReceivedRef.current = { sessions: false, journal: false, friends: false, requestsReceived: false, requestsSent: false };
            }
        };
    }, [userId, activeProfileId, isLoadingProfiles]); // Dependencies

    // Return the state managed by this hook
    return {
        activeSessions, activeJournal, activeFriends, activeRequestsReceived, activeRequestsSent,
        isLoadingActiveSubcollections, subcollectionError
    };
}; // End of useSubcollectionListeners hook